import FormatListBulletedRoundedIcon from "@mui/icons-material/FormatListBulletedRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ShowChartRoundedIcon from "@mui/icons-material/ShowChartRounded";
import {Box, Button, CircularProgress, Stack, ToggleButton, ToggleButtonGroup} from "@mui/material";
import {alpha, useTheme} from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import type {AggregationSurveyEvaluationQuestion}
    from "crqlar-react-common/dist/models/aggregation/surveyAggregation.model";
import {SurveyQuestion} from "crqlar-react-common/dist/models/survey/surveyQuestion.model";
import React, {useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {SurveyQuestionTypeElement} from "../../common/uiElements/SurveyQuestionTypeElement";
import {CardHeadline} from "../../insights/kit/Headline";
import {directionColor, signed, useInsightsFormatters} from "../../insights/kit/insightsFormat";
import {InsightsLegend, LegendEntry} from "../../insights/kit/InsightsLegend";
import {InsightsTooltip, TooltipRow, TooltipTitle} from "../../insights/kit/InsightsTooltip";
import {useBarGrowth} from "../insights/base/useBarGrowth";
import {BAR_DRAW_DURATION, BAR_HEIGHT, BAR_RADIUS, COMPARE_BAR_HEIGHT} from "../insights/constants";
import {InsightsCard} from "../../insights/kit/InsightsCard";
import {InsightsColumn, InsightsTable} from "../../insights/kit/InsightsTable";
import {SurveyAnswerContext, surveyQuestionTitle} from "../shared/surveyAnswer.util";
import SurveyQuestionMarkers from "../shared/SurveyQuestionMarkers";
import {useRevealedSurveyEvaluation} from "../shared/useSurveyEvaluation";
import SurveyEvaluationAnswersDialog, {SurveyEvaluationAnswersBucket} from "./SurveyEvaluationAnswersDialog";
import SurveyEvaluationWrittenAnswers from "./SurveyEvaluationWrittenAnswers";
import {
    EVALUATION_CONTENT_MAX_HEIGHT,
    evaluationBuckets,
    EvaluationLabelWidth,
    isDateQuestion,
    isTextQuestion,
    isUploadQuestion
} from "./surveyEvaluation.util";

export type SurveyEvaluationViewMode = 'chart' | 'list';

interface Props {
    question: SurveyQuestion
    index: number
    evaluation?: AggregationSurveyEvaluationQuestion
    ctx: SurveyAnswerContext
    currentCaption: string
    compareCaption: string
    view: SurveyEvaluationViewMode
    onViewChange: (next: SurveyEvaluationViewMode) => void
    labelWidth: EvaluationLabelWidth
}

const CARD_PADDING = 2.5;

const scrollArea = {maxHeight: EVALUATION_CONTENT_MAX_HEIGHT, overflowY: 'auto', minWidth: 0} as const;

const scrollBox = {...scrollArea, mr: -CARD_PADDING, pr: CARD_PADDING} as const;

interface Row {
    key: string
    label: string
    count: number
    compareCount: number
    delta: number | null
    share: number
    compareShare: number
}

interface RowLabels {
    current: string
    compare: string
}

interface BarProps {
    share: number
    height: number
    color: string
    grown: boolean
}

/** One rail: a tinted track with the value drawn across it, on the insights bar geometry. */
function Bar({share, height, color, grown}: BarProps) {
    return (
        <Box sx={{position: 'relative', height, minWidth: 0}}>
            <Box
                sx={theme => ({
                    position: 'absolute',
                    inset: 0,
                    borderRadius: `${BAR_RADIUS}px`,
                    bgcolor: alpha(theme.palette.text.primary, 0.05),
                })}
            />

            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: 0,
                    width: `${grown ? share : 0}%`,
                    borderRadius: `${BAR_RADIUS}px`,
                    bgcolor: color,
                    transition: `width ${BAR_DRAW_DURATION}ms ease-out`,
                }}
            />
        </Box>
    );
}

interface AnswerRowProps {
    row: Row
    labels: RowLabels
    hasCompare: boolean
    grown: boolean
    compareColor: string
    labelWidth: EvaluationLabelWidth
    formatCount: (value: number) => string
    /** Lists the guests behind the row - an empty list for a row nobody answered. */
    onOpen?: () => void
}

/**
 * One bucket on one line - label, rail, count - because the labels here are a score or an option,
 * not a sentence. The kit's cards put the label on its own line above the bar; that reads well for
 * themes and wastes half the height for a 1-10 distribution.
 */
function AnswerRow({row, labels, hasCompare, grown, compareColor, labelWidth, formatCount, onOpen}: AnswerRowProps) {
    const theme = useTheme();

    const tooltip = (
        <React.Fragment>
            <TooltipTitle title={row.label}/>
            <TooltipRow swatch={theme.palette.primary.main} label={labels.current} value={formatCount(row.count)}/>

            {hasCompare && (
                <TooltipRow swatch={compareColor} label={labels.compare} value={formatCount(row.compareCount)}/>
            )}
        </React.Fragment>
    );

    const change = row.delta == null ? null : signed(row.delta, formatCount);

    return (
        <InsightsTooltip followCursor placement={'top'} title={tooltip}>
            <Stack
                direction={'row'}
                alignItems={'center'}
                gap={1.5}
                {...(onOpen && {
                    role: 'button',
                    tabIndex: 0,
                    'aria-label': row.label,
                    onClick: onOpen,
                    onKeyDown: (event: React.KeyboardEvent) => {
                        if (event.key !== 'Enter' && event.key !== ' ') return;
                        event.preventDefault();
                        onOpen();
                    },
                })}
                sx={theme => ({
                    minWidth: 0,
                    cursor: onOpen ? 'pointer' : 'default',
                    px: 1,
                    mx: -1,
                    py: 0.5,
                    borderRadius: 1.5,
                    transition: 'background-color 120ms ease',
                    '&:hover': {backgroundColor: alpha(theme.palette.text.primary, 0.05)},
                })}
            >
                <Typography
                    noWrap
                    title={row.label}
                    sx={{width: labelWidth, flexShrink: 0, minWidth: 0, fontSize: 13, color: 'text.secondary'}}
                >
                    {row.label}
                </Typography>

                <Stack gap={0.5} sx={{flexGrow: 1, minWidth: 0}}>
                    <Bar share={row.share} height={BAR_HEIGHT} color={theme.palette.primary.main} grown={grown}/>

                    {hasCompare && (
                        <Bar share={row.compareShare} height={COMPARE_BAR_HEIGHT} color={compareColor} grown={grown}/>
                    )}
                </Stack>

                <Typography
                    sx={{
                        width: 28,
                        flexShrink: 0,
                        textAlign: 'right',
                        fontSize: 14,
                        fontWeight: 700,
                        fontVariantNumeric: 'tabular-nums',
                        color: row.count > 0 ? 'text.primary' : 'text.disabled',
                    }}
                >
                    {formatCount(row.count)}
                </Typography>

                {hasCompare && (
                    <Typography
                        sx={{
                            width: 40,
                            flexShrink: 0,
                            textAlign: 'right',
                            fontSize: 13,
                            fontWeight: 700,
                            fontVariantNumeric: 'tabular-nums',
                            color: change == null ? 'text.disabled' : directionColor(change.direction),
                        }}
                    >
                        {change == null ? '—' : change.text}
                    </Typography>
                )}
            </Stack>
        </InsightsTooltip>
    );
}

interface ViewToggleProps {
    view: SurveyEvaluationViewMode
    onChange: (next: SurveyEvaluationViewMode) => void
}

/** The kit's chart/list switch, kept local so the card body is sized by its rows, not by CHART_HEIGHT. */
function ViewToggle({view, onChange}: ViewToggleProps) {
    const {t} = useTranslation();

    return (
        <ToggleButtonGroup
            exclusive
            size={'small'}
            value={view}
            onChange={(_event, next: SurveyEvaluationViewMode | null) => next && onChange(next)}
        >
            <ToggleButton value={'chart'} aria-label={t('Chart')}>
                <ShowChartRoundedIcon fontSize={'small'}/>
            </ToggleButton>

            <ToggleButton value={'list'} aria-label={t('List')}>
                <FormatListBulletedRoundedIcon fontSize={'small'}/>
            </ToggleButton>
        </ToggleButtonGroup>
    );
}

/**
 * One question of the evaluation tab, built out of the insights kit: `SeriesCard` for the outlined
 * container and its chart/list toggle, the shared bar geometry for the distribution, `InsightsTable`
 * for the list view. Nothing here styles a card of its own - a question that reads differently
 * (free text, dates) drops to `InsightsCard`, the same container without the toggle.
 */
function SurveyEvaluationQuestionCard({
    question,
    index,
    evaluation,
    ctx,
    currentCaption,
    compareCaption,
    view,
    onViewChange,
    labelWidth
}: Props) {
    const {t} = useTranslation();
    const theme = useTheme();
    const {count: formatCount} = useInsightsFormatters();
    const [revealed, setRevealed] = useState(false);
    // The bar whose guests are listed; null while the dialog is closed.
    const [listed, setListed] = useState<SurveyEvaluationAnswersBucket | null>(null);

    const hasCompare = !!evaluation?.compareValueCounts || !!evaluation?.compareTextAnswers;
    const isText = isTextQuestion(question);
    const isDate = isDateQuestion(question);
    const isUpload = isUploadQuestion(question);
    const sensitive = !!question.isSensitive;

    /*
     * The evaluation the page runs on has the sensitive answers cut out of it, so revealing them is
     * a second fetch rather than a flag on what is already here - there is nothing local to unhide.
     */
    const reveal = useRevealedSurveyEvaluation(sensitive && revealed);
    const revealedFold = reveal.evaluation?.questions.find(entry => entry.questionId === question.id);
    const showingSensitive = sensitive && revealed && revealedFold != null;

    const grown = useBarGrowth(hasCompare);
    const compareColor = theme.palette.grey[400];

    const buckets = useMemo(
        () => evaluationBuckets(question, ctx, evaluation?.valueCounts, evaluation?.compareValueCounts),
        [question, ctx, evaluation?.valueCounts, evaluation?.compareValueCounts]
    );

    const answeredCount = evaluation?.answeredCount ?? 0;
    const compareAnsweredCount = evaluation?.compareAnsweredCount ?? 0;
    const shownFold = showingSensitive ? revealedFold : evaluation;
    const writtenAnswerCount = shownFold?.textAnswers.length ?? 0;
    const compareWrittenAnswerCount = shownFold?.compareTextAnswers?.length ?? 0;

    const rows = useMemo<Row[]>(() => {
        const valueCounts = evaluation?.valueCounts ?? {};
        const compareValueCounts = evaluation?.compareValueCounts ?? null;

        const counted = buckets.map(bucket => ({
            key: bucket.key,
            label: bucket.label,
            count: valueCounts[bucket.key] ?? 0,
            compareCount: compareValueCounts?.[bucket.key] ?? 0,
        }));

        // One scale for both rails, so a bar means the same length in either period.
        const scaleBase = counted.reduce(
            (max, row) => Math.max(max, row.count, hasCompare ? row.compareCount : 0),
            0
        ) || 1;

        return counted.map(row => ({
            ...row,
            delta: hasCompare ? row.count - row.compareCount : null,
            share: (row.count / scaleBase) * 100,
            compareShare: (row.compareCount / scaleBase) * 100,
        }));
    }, [buckets, evaluation?.valueCounts, evaluation?.compareValueCounts, hasCompare]);

    const title = surveyQuestionTitle(question, ctx);
    const answersLabel = (count: number) => t('Answer count', {count});
    const columnCaption = (base: string, count: number) => `${base} · ${answersLabel(count)}`;

    const currentColumnCaption = columnCaption(currentCaption, answeredCount);
    const compareColumnCaption = columnCaption(compareCaption, compareAnsweredCount);

    const rowLabels: RowLabels = {current: currentCaption, compare: compareCaption};

    const headline = (
        <CardHeadline
            value={formatCount(answeredCount)}
            suffix={t('answers')}
            change={hasCompare ? {
                title: t('Answers'),
                current: formatCount(answeredCount),
                previous: formatCount(compareAnsweredCount),
                delta: answeredCount - compareAnsweredCount,
                formatDelta: formatCount,
                currentLabel: currentCaption,
                compareLabel: compareCaption,
            } : null}
        />
    );

    const actions = (
        <React.Fragment>
            <SurveyQuestionMarkers question={question} size={'standalone'} gap={0}/>

            <SurveyQuestionTypeElement type={question.type}/>
        </React.Fragment>
    );

    const legend = hasCompare ? (
        <InsightsLegend>
            <LegendEntry shape={'bar'} color={theme.palette.primary.main} label={currentCaption}/>
            <LegendEntry shape={'bar'} color={compareColor} label={compareCaption}/>
        </InsightsLegend>
    ) : undefined;

    const distribution = (
        <Stack gap={0.5} sx={scrollBox}>
            {rows.map(row => (
                <AnswerRow
                    key={row.key}
                    row={row}
                    labels={rowLabels}
                    hasCompare={hasCompare}
                    grown={grown}
                    compareColor={compareColor}
                    labelWidth={labelWidth}
                    formatCount={formatCount}
                    onOpen={() => setListed({key: row.key, label: row.label})}
                />
            ))}
        </Stack>
    );

    const columns: Array<InsightsColumn<Row> | false> = [
        {key: 'answer', kind: 'label', label: t('Answer'), cell: row => row.label},
        {
            key: 'count',
            kind: 'value',
            label: hasCompare ? currentColumnCaption : t('Results'),
            cell: row => row.count,
            format: formatCount
        },
        hasCompare && {
            key: 'compare',
            kind: 'compare',
            label: compareColumnCaption,
            cell: row => row.compareCount,
            format: formatCount
        },
        hasCompare && {
            key: 'change',
            kind: 'delta',
            label: t('Change'),
            cell: row => row.delta,
            format: formatCount,
            comparison: true
        },
    ];

    const table = (
        <InsightsTable
            label={t('Answer')}
            columns={columns}
            rows={rows}
            rowKey={row => row.key}
        />
    );

    const emptyLabel = (
        <Typography variant={'body2'} color={'text.secondary'}>
            {t('No answers in this period.')}
        </Typography>
    );

    const gate = (
        <Stack
            direction={'row'}
            alignItems={'center'}
            flexWrap={'wrap'}
            gap={1.5}
            sx={{p: 1.5, borderRadius: 1.5, border: '1px dashed', borderColor: 'divider'}}
        >
            <LockOutlinedIcon sx={{fontSize: 20, color: 'warning.main', flexShrink: 0}}/>

            <Typography variant={'body2'} color={'text.secondary'} sx={{flex: '1 1 auto', minWidth: 0}}>
                {t('Sensitive answers are hidden by default.')}
            </Typography>

            {reveal.loading && <CircularProgress size={16} sx={{flexShrink: 0}}/>}

            <Button
                size={'small'}
                disabled={!reveal.available || reveal.loading}
                onClick={() => setRevealed(true)}
                sx={{flexShrink: 0}}
            >
                {t('Show answers')}
            </Button>
        </Stack>
    );

    const writtenAnswersInPeriod = (period: 'current' | 'compare', caption?: string) => (
        <Stack gap={1} sx={{flex: 1, minWidth: 0, width: '100%'}}>
            {caption && <Typography variant={'caption'} color={'text.secondary'}>{caption}</Typography>}
            <Box sx={hasCompare ? scrollArea : scrollBox}>
                <SurveyEvaluationWrittenAnswers
                    question={question}
                    period={period}
                    includeSensitive={showingSensitive}
                />
            </Box>
        </Stack>
    );

    const writtenAnswerLists = (current: string, previous: string) => {
        // The gate comes first: the withheld answers arrive as an empty list, not as a marked one.
        if (sensitive && !showingSensitive) return gate;

        const body = !writtenAnswerCount && !compareWrittenAnswerCount
            ? emptyLabel
            : !hasCompare
                ? writtenAnswersInPeriod('current')
                : (
                    <Stack direction={{xs: 'column', sm: 'row'}} gap={3} alignItems={'flex-start'}>
                        {writtenAnswersInPeriod('current', current)}
                        {writtenAnswersInPeriod('compare', previous)}
                    </Stack>
                );

        if (!sensitive) return body;

        return (
            <Stack gap={1} alignItems={'flex-start'}>
                <Button
                    size={'small'}
                    color={'inherit'}
                    startIcon={<LockOutlinedIcon sx={{fontSize: 18}}/>}
                    onClick={() => setRevealed(false)}
                    sx={{color: 'text.secondary'}}
                >
                    {t('Hide answers')}
                </Button>

                {body}
            </Stack>
        );
    };

    /*
     * A sensitive question always carries the block: with the answers withheld there is nothing to
     * count, so the only honest thing to show is the gate that fetches them.
     */
    const writtenAnswers = (sensitive || writtenAnswerCount > 0 || compareWrittenAnswerCount > 0) && (
        <Stack gap={1.5} sx={{mt: 3}}>
            <Typography variant={'body2'} fontWeight={600}>
                {t('Guest-written answers')}
            </Typography>

            {writtenAnswerLists(
                columnCaption(currentCaption, writtenAnswerCount),
                columnCaption(compareCaption, compareWrittenAnswerCount)
            )}
        </Stack>
    );

    const cardTitle = `${index}. ${title}`;

    // Free text and dates have no distribution to switch between: same container, no toggle.
    const togglable = !isText && !isDate && rows.length > 0;
    const showList = view === 'list';

    /*
     * An upload is only counted, so its headline is the whole card. It says nobody answered only when
     * neither period has an answer, the same rule the export follows.
     */
    const body = isText
        ? writtenAnswerLists(currentColumnCaption, compareColumnCaption)
        : isUpload
            ? (answeredCount === 0 && compareAnsweredCount === 0 ? emptyLabel : null)
            : rows.length === 0
                ? emptyLabel
                : isDate || showList
                    ? <Box sx={scrollBox}>{table}</Box>
                    : distribution;

    return (
        <InsightsCard
            title={cardTitle}
            headline={headline}
            actions={(
                <React.Fragment>
                    {actions}
                    {togglable && <ViewToggle view={view} onChange={onViewChange}/>}
                </React.Fragment>
            )}
            footer={togglable && !showList ? legend : undefined}
        >
            {body}

            {/* An upload has no text, so a sensitive one has nothing behind the gate to fetch. */}
            {!isText && !isUpload && writtenAnswers}

            {listed && (
                <SurveyEvaluationAnswersDialog
                    question={question}
                    number={index}
                    bucket={listed}
                    ctx={ctx}
                    includeSensitive={showingSensitive}
                    onClose={() => setListed(null)}
                />
            )}
        </InsightsCard>
    );
}

export default SurveyEvaluationQuestionCard;
